/** **「이미 배경이 지워진 사진인가」를 무엇을 보고 말하는가.** `docs/PRD.md` `7.13` **D-89**.
 *
 * `A-21b`(앱) · `W-20b`(웹) 시트가 이 판정 하나로 뜬다. 그 시트가 요구하는 결과의 모양은
 * **걸린 장의 수** 하나다(`7.6.5a`).
 *
 * ## ⭐ 갈래 셋 중 ⓑ 를 골랐다 — **시트가 서는 자리가 정했다**
 *
 * D-89 의 갈래는 ⓐ 서버가 머리만 읽음 · ⓑ 클라이언트가 담기 전에 읽음 · ⓒ `services/imaging`
 * 에 물음, 셋이었다.
 *
 * | | 값 | 버린 이유 |
 * |---|---|---|
 * | ⓐ 서버가 업로드 경로에서 머리를 읽는다 | 공짜(이미 읽는 33바이트 안에 있다) | ⛔ **시트보다 늦게 온다.** 시트는 `담기`(`A-21`)에 서는데 **담기는 서버에 아무것도 안 보낸다** — 서버로 가는 것은 그 다음 `올리기`다. 셀러가 이미 담고 올린 뒤에 나오는 사실이라, `7.6.5a` 가 결제 직전 자리를 버린 것과 **같은 이유로** 못 쓴다. 게다가 머리만으로는 거짓 양성이 쏟아진다(아래) |
 * | ⓒ `services/imaging` 에 묻는다 | 정확 | ⛔ **담기 전에 서버 왕복**이 생기고, 그러려면 **담기지도 않은 사진의 전체 바이트를 먼저 올려야 한다.** `7.6.5a` 가 가른 `담기 ≠ 올리기` 를 판정이 깨뜨린다. 비행기 모드에서 담기가 막힌다 |
 * | **ⓑ 클라이언트가 담기 전에 읽는다** | **시트가 서는 바로 그 순간** · 서버 비용 0 · 장당 과금 0 | **골랐다** |
 *
 * ⚠️ **ⓑ 의 실측 함정**: iOS `expo-image-picker` 의 `asset.base64` 는 **언제나 JPEG** 라
 * 알파가 없다(`apps/mobile/src/app/dev/composition-lab.tsx` 머리말이 그 실측을 들고 있다).
 * **`asset.uri` 가 가리키는 파일에서 바이트를 읽어야 한다.**
 *
 * ## ⭐ 무엇을 근거로 「지워졌다」고 말하는가
 *
 * ```
 * PNG 서명   ≠  알파 있음
 * 알파 있음   ≠  배경이 지워짐
 * ```
 *
 * **투명 픽셀이 한 장도 없는 RGBA PNG 가 흔하다**(스크린샷, 일부 편집기 출력).
 * 그래서 이 파일은 **두 층**으로 판정한다.
 *
 * 1. **자격** — 그 형식이 알파를 담을 수 있는가(`readAlphaCapability`). 머리 몇십 바이트.
 *    JPEG 는 여기서 끝난다.
 * 2. **근거** — 실제 알파가 **배경이 지워진 모양인가**(`judgeAlphaMeasurement`). 조건 셋을
 *    **전부** 만족해야 한다:
 *    - **테두리가 비었다** — 가장자리 한 줄의 `{@link PRECUT_MIN_BORDER_CLEARED}` 이상이 투명.
 *      ⭐ **이 조건 하나가 「전부 불투명한 RGBA PNG」를 통째로 떨군다**(테두리 투명 0%).
 *    - **지워진 양이 있다** — 전체 투명 비율 `{@link PRECUT_MIN_TRANSPARENT}` 이상.
 *      ⭐ **테두리 1px 만 투명한 사진**(일부 편집기가 그렇게 뱉는다)을 이 조건이 떨군다.
 *      테두리 조건만 있으면 그 장이 100% 로 통과한다.
 *    - **남은 것이 있다** — 가운데 절반의 `{@link PRECUT_MIN_SUBJECT}` 이상이 불투명.
 *      ⭐ **통째로 빈 PNG** 를 이 조건이 떨군다. 앞 둘만으로는 빈 장이 만점으로 통과한다.
 *
 * ## ⛔ 거짓 양성이 거짓 음성보다 비싸다 — 그래서 **모르면 안 띄운다**
 *
 * 안 지워진 사진에 시트를 띄우면 **셀러가 시트를 읽지 않고 닫는 법을 배운다**(`7.6.6` 의
 * `A-22` 가 적어 둔 것). 그러면 진짜일 때도 안 읽는다. 게다가 `7.6.5a` 가 적었듯 **멀쩡한
 * 장을 뺀다.**
 *
 * **그래서 `UNKNOWN` 은 `PRECUT` 쪽이 아니라 「안 띄운다」 쪽이다**(`countPrecut` 이 안 센다).
 * 읽지 못한 형식, 인터레이스 PNG, 상한을 넘는 크기 — 전부 `UNKNOWN` 이고 **시트가 안 뜬다.**
 * 못 띄운 값은 **셀러가 ₩500 을 한 장 더 쓰는 것**이고, 잘못 띄운 값은 **시트 전체를 못 믿게
 * 되는 것**이다. 뒤엣것이 비싸다.
 *
 * ## ⛔ 이 낱말들은 코드 전용이다
 *
 * `PRECUT`·`NOT_PRECUT`·`UNKNOWN` 은 **셀러 화면에 나가지 않는다.** 화면 낱말은
 * `배경 지우기` 계열로 이미 잠겨 있다(`docs/PRD.md` `7.5.9`. `누끼`는 금지어다).
 *
 * ## 비용
 *
 * PNG 는 **실제로 푼다**(`inflate` 를 이 파일이 들고 있다 — Hermes 에 `DecompressionStream`
 * 이 없어서 세 런타임이 같은 코드를 쓰려면 다른 길이 없다). 그래서 **화소 수에 상한을 건다**
 * (`{@link PRECUT_MAX_PIXELS}`). 넘으면 `UNKNOWN` 이고, **부르는 쪽이 먼저 줄여서 넘겨야 한다**
 * (앱은 `expo-image-manipulator`, 웹은 캔버스. 웹은 캔버스가 알파를 바로 주므로 PNG 를 풀
 * 필요도 없다 → `judgePrecutFromRgba`).
 *
 * ⛔ **이 파일을 요청 경로(Workers)에서 부르지 마라.** 장당 CPU 가 드는 코드이고, 판정이
 * 필요한 자리는 담기(클라이언트)뿐이다.
 *
 * ## ⛔ 이 판정은 **D1 에 저장하지 않는다** (2026-09-24 `data-manager` 판정)
 *
 * `JobImage` 에 칸을 두지 않고 **업로드 요청에도 싣지 않는다. 마이그레이션 0건이다.**
 * 근거 넷은 `docs/DATA_DICTIONARY.md` **32장**에 있고 요약은 이것이다.
 *
 * 1. **판정이 나는 시점에 붙일 행이 없다.** 담기는 서버에 아무것도 안 보낸다. `DetailJob`
 *    은 그다음 화면(`A-22`)에서 생기고 `JobImage` 는 그보다 더 뒤다. `JobEvent` 도 자리가
 *    아니다(`jobId` 가 `NOT NULL`).
 * 2. ⭐ **칸이 세는 수가 「시트를 다시 볼 조건」이 묻는 수가 아니다.** `다시 고르기` 를 누른
 *    장은 **영영 행이 안 생긴다.** 그러면 **시트가 제 일을 한 경우만 빠진 집계**가 되고,
 *    시트가 잘 들을수록 "흔하지 않다"고 보이게 된다.
 * 3. **올라간 바이트와 판정한 바이트가 같다는 보장이 없다**(바로 아래).
 * 4. ⛔ **셀러가 바꿀 수 있는 값이다.** 청구·환불·권한 판정의 입력으로 쓰지 마라.
 *
 * ⚠️ **그래서 판정은 변환한 뒤 파일을 봐야 한다.** `apps/mobile/src/lib/prepare-image.ts` 의
 * `preparePhoto` 가 허용되지 않는 형식을 **JPEG 로 다시 인코딩**하고, **JPEG 는 알파를 못
 * 담는다.** 변환 앞에서 판정하면 **서버에 올라간 적 없는 바이트**를 두고 셀러에게 말하게 된다.
 *
 * ⭐ **원가를 설명할 값이 필요해지면 그것은 이 값이 아니라 서버가 직접 읽은 값이다**
 * (`docs/DATA_DICTIONARY.md` 32.4 의 **M-24**. 승인 후보가 아니고 지금 필요 없다).
 * ⛔ **클라이언트가 보낸 판정을 그 칸에 담지 마라.** 그것이 `JobEvent.costAmount` 가 낸
 * 사고의 다음 판이다(같은 문서 14.7 · 15.2).
 */

// ── 판정 문턱 ───────────────────────────────────────────────────────────────

/** **이 값 아래면 「투명」이다.** 0 이 아니라 16 인 이유: 누끼 도구 출력의 배경이 정확히 0 이
 *  아니라 1~8 로 남는 경우가 있다(8비트 반올림). ⛔ 이 값을 키우면 **반투명이 투명으로 읽혀**
 *  가장자리가 흐린 사진이 누끼가 된다. */
export const ALPHA_TRANSPARENT_BELOW = 16;

/** **이 값 이상이면 「불투명」이다.** 255 가 아닌 이유는 위와 같다. */
export const ALPHA_OPAQUE_AT_LEAST = 240;

/** **가장자리 한 줄이 이만큼 비어 있어야 한다.** 누끼는 물체가 안쪽에 서고 **테두리가 배경**
 *  이다. 1.0 이 아닌 이유: 바운딩 박스로 자른 누끼는 **물체가 한 변에 닿는다.** */
export const PRECUT_MIN_BORDER_CLEARED = 0.9;

/** **전체에서 지워진 양의 하한.** ⛔ 이것이 없으면 **테두리 1px 만 투명한 불투명 사진**이
 *  테두리 조건 100% 로 통과한다. */
export const PRECUT_MIN_TRANSPARENT = 0.1;

/** **가운데 절반에 남아 있어야 하는 양.** ⛔ 이것이 없으면 **통째로 빈 PNG** 가 통과한다.
 *  0.5 가 아니라 0.15 인 이유: 목걸이·의자처럼 **가는 물체의 누끼**를 떨구면 안 된다. */
export const PRECUT_MIN_SUBJECT = 0.15;

/** **가운데**로 볼 영역. 가로·세로 가운데 50%. */
const CENTER_FROM = 0.25;
const CENTER_TO = 0.75;

/** 이보다 작으면 판정하지 않는다. 테두리 한 줄과 가운데 절반이 겹쳐 버려 두 비율이 같은
 *  픽셀을 센다. */
export const PRECUT_MIN_SIDE = 8;

/** **부르기 전에 줄여 둘 긴 변.** 이 크기면 테두리 한 줄이 1,020 표본이라 비율 셋을 재기에
 *  차고 넘친다. ⭐ **앱·웹이 같은 수를 써야** 두 화면이 같은 답을 낸다. */
export const PRECUT_SAMPLE_LONG_SIDE = 256;

/** **풀어서 볼 화소 수의 상한.** 넘으면 `UNKNOWN` 이고, **부르는 쪽이 줄여서 다시 부르면 된다.**
 *
 * ## 값의 근거는 실측이다 (2026-09-24 · `test/fixtures/precut.bench.mjs` · Node 24 / V8)
 *
 * 최악(RGBA 8비트 · Paeth 필터 · 압축이 잘 안 되는 사진)으로 잰 값이다.
 *
 * | 화소 | 파일 | 장당 | **15장** |
 * |---|---|---|---|
 * | 65,536 (256²) | 192KB | **13.9ms** | 209ms |
 * | **262,144 (512²)** | 764KB | **52.2ms** | **783ms** |
 * | 1,048,576 (1024²) | 3.0MB | 192ms | 2.9초 |
 * | 1,200,000 | 3.5MB | 218ms | 3.3초 |
 *
 * 화소당 약 **180ns** 로 곧게 늘어난다. ⚠️ **이 값은 V8 이고 실제로 도는 곳은 Hermes(앱)**
 * 라 몇 배 더 걸린다. 그래서 상한을 `512²` 에 둔다 — **V8 에서 15장에 0.8초**면 Hermes 에서도
 * 담기 버튼 아래 기다림이 몇 초를 넘지 않는다.
 *
 * ⛔ **원본 12MP 사진을 그대로 풀게 만들지 마라.** 그 크기면 장당 2초가 넘고 15장이면 30초다.
 * **부르는 쪽이 먼저 줄이는 것이 설계다**(`{@link PRECUT_SAMPLE_LONG_SIDE}`) — 네이티브 축소는
 * 공짜에 가깝고 이 코드는 아니다. 줄이면 **가장자리가 섞이지만 비율 셋은 그대로 남는다**
 * (테두리가 통째로 투명한 사진은 줄여도 통째로 투명하다). */
export const PRECUT_MAX_PIXELS = 262_144;

// ── 결과 ───────────────────────────────────────────────────────────────────

/** ⛔ **셀러 화면에 나가지 않는 코드다.** 파일 머리말의 낱말 규칙을 본다.
 *
 * ⚠️ **`PRECUT` 은 「이미 지워진」이지 「지우기 전」이 아니다.** 이 파이프라인에서는 **모든
 * 원본이 누끼 전**이라 `pre-` 를 반대로 읽을 여지가 있다. **이 값이 참이면 셀러가 이미
 * 지워서 가져온 것**이고, 그래서 시트가 뜬다. 짝인 `NOT_PRECUT` 이 그 뜻을 한 번 더 고정한다.
 * (이름 판정의 근거는 `docs/DATA_DICTIONARY.md` 32.6.) */
export type PrecutVerdict = "PRECUT" | "NOT_PRECUT" | "UNKNOWN";

/** **왜 그렇게 판정했는가.** 비민감 코드만이다 — 파일 이름도 바이트도 담지 않는다. */
export type PrecutReason =
  /** 알파를 담을 수 없는 형식이다(JPEG 등). `NOT_PRECUT`. */
  | "NO_ALPHA_CHANNEL"
  /** 조건 셋을 다 넘었다. `PRECUT`. */
  | "BACKGROUND_CLEARED"
  /** 알파는 있는데 **테두리가 안 비었다.** `NOT_PRECUT`. */
  | "BORDER_NOT_CLEARED"
  /** 테두리는 비었는데 **지워진 양이 없다시피 하다.** `NOT_PRECUT`. */
  | "TOO_LITTLE_TRANSPARENT"
  /** 다 지워지고 **남은 것이 없다.** `NOT_PRECUT`. */
  | "NOTHING_LEFT"
  /** 형식을 못 알아봤다. `UNKNOWN`. */
  | "UNREADABLE"
  /** 알파를 담을 수 있는 형식인데 **화소를 못 읽었다**(인터레이스·미지원 비트깊이·WebP 본문).
   *  `UNKNOWN`. */
  | "PIXELS_UNREAD"
  /** 화소 수가 상한을 넘었다. **부르는 쪽이 줄여서 다시 부르면 된다.** `UNKNOWN`. */
  | "TOO_MANY_PIXELS"
  /** 판정하기에 너무 작다. `UNKNOWN`. */
  | "TOO_SMALL";

/** 실제로 잰 값. **화면에 안 나간다** — 문턱을 흔들 때 근거를 보기 위한 값이고, 시험이 이
 *  숫자를 직접 단정한다(문턱만 단정하면 문턱을 바꾼 변이를 못 잡는다). */
export interface AlphaMeasurement {
  width: number;
  height: number;
  /** 전체에서 `alpha < {@link ALPHA_TRANSPARENT_BELOW}` 인 비율. */
  transparent: number;
  /** 가장자리 한 줄에서 투명한 비율. */
  borderCleared: number;
  /** 가운데 절반에서 `alpha >= {@link ALPHA_OPAQUE_AT_LEAST}` 인 비율. */
  subject: number;
}

export interface PrecutAssessment {
  verdict: PrecutVerdict;
  reason: PrecutReason;
  /** 못 읽었으면 `null`. */
  measured: AlphaMeasurement | null;
}

function no(reason: PrecutReason, measured: AlphaMeasurement | null = null): PrecutAssessment {
  return { verdict: "NOT_PRECUT", reason, measured };
}

function unknown(reason: PrecutReason): PrecutAssessment {
  return { verdict: "UNKNOWN", reason, measured: null };
}

/** **시트가 세는 수.** ⛔ `UNKNOWN` 을 세지 않는다 — 파일 머리말의 이유 그대로다.
 *
 * `0` 이면 시트가 뜨지 않는다. 뜨는 조건이 곧 `countPrecut(...) > 0` 이다. */
export function countPrecut(assessments: readonly { verdict: PrecutVerdict }[]): number {
  let n = 0;
  for (const a of assessments) if (a.verdict === "PRECUT") n += 1;
  return n;
}

// ── 1층: 자격 (머리 몇십 바이트) ────────────────────────────────────────────

/** 그 형식이 **알파를 담을 수 있는가.** */
export type AlphaCapability =
  /** 담을 수 없다. 더 볼 것 없이 `NOT_PRECUT`. */
  | "NONE"
  /** 담을 수 있다. **여기서 끝내면 안 된다** — 2층 근거가 있어야 `PRECUT` 이다. */
  | "POSSIBLE"
  /** 형식을 못 알아봤다. */
  | "UNREADABLE";

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function isPng(b: Uint8Array): boolean {
  if (b.length < 33) return false;
  for (let i = 0; i < 8; i += 1) if (b[i] !== PNG_SIGNATURE[i]) return false;
  // 첫 청크는 규격상 반드시 IHDR 이다.
  return b[12] === 0x49 && b[13] === 0x48 && b[14] === 0x44 && b[15] === 0x52;
}

function fourcc(b: Uint8Array, at: number): string {
  return String.fromCharCode(b[at] ?? 0, b[at + 1] ?? 0, b[at + 2] ?? 0, b[at + 3] ?? 0);
}

/** PNG 청크를 훑어 `tRNS` 가 있는지 본다. **`IDAT` 를 만나면 멈춘다** — `tRNS` 는 규격상
 *  `IDAT` 앞에 와야 한다. 그래서 큰 파일이어도 앞쪽 몇백 바이트에서 끝난다. */
function hasTrns(b: Uint8Array): boolean {
  let at = 8;
  while (at + 8 <= b.length) {
    const len = readU32(b, at);
    const type = fourcc(b, at + 4);
    if (type === "tRNS") return true;
    if (type === "IDAT" || type === "IEND") return false;
    at += 12 + len;
  }
  return false;
}

function readU32(b: Uint8Array, at: number): number {
  return ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
}

/** **머리만 보고** 그 파일이 알파를 담을 수 있는지 답한다. 화소를 풀지 않는다.
 *
 * ⛔ **이 함수의 답만으로 시트를 띄우지 마라.** `POSSIBLE` 은 「알파 채널이 있다」일 뿐이고
 * **전부 불투명한 RGBA PNG 가 여기서 `POSSIBLE` 로 나온다.** 그것이 D-89 가 ⓐ 를 버린 이유다. */
export function readAlphaCapability(bytes: Uint8Array): AlphaCapability {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    // JPEG. 알파가 없다. **이 한 줄이 앨범 사진 대부분을 공짜로 끝낸다.**
    return "NONE";
  }
  if (isPng(bytes)) {
    const colorType = bytes[25];
    // 4 = 회색+알파, 6 = RGBA. 채널로 들고 있다.
    if (colorType === 4 || colorType === 6) return "POSSIBLE";
    // 0 = 회색, 2 = RGB, 3 = 팔레트. 알파가 있다면 `tRNS` 에 있다.
    if (colorType === 0 || colorType === 2 || colorType === 3) {
      return hasTrns(bytes) ? "POSSIBLE" : "NONE";
    }
    return "UNREADABLE";
  }
  if (bytes.length >= 16 && fourcc(bytes, 0) === "RIFF" && fourcc(bytes, 8) === "WEBP") {
    const type = fourcc(bytes, 12);
    // `VP8X` 의 깃발 바이트에 ALPHA 비트가 있다.
    if (type === "VP8X") return (bytes[20]! & 0x10) !== 0 ? "POSSIBLE" : "NONE";
    // `VP8 ` 는 손실 단독이라 알파가 없다. `VP8L` 은 있을 수 있다.
    if (type === "VP8 ") return "NONE";
    if (type === "VP8L") return "POSSIBLE";
    return "UNREADABLE";
  }
  return "UNREADABLE";
}

// ── 2층: 근거 (실제 알파) ──────────────────────────────────────────────────

/** 잰 값으로 판정한다. **조건 셋을 전부 넘어야 `PRECUT` 이다.**
 *
 * 순서가 곧 진단이다 — 먼저 걸린 조건이 `reason` 이 된다. 테두리를 먼저 보는 이유는
 * **그 조건이 가장 많이 떨구기 때문**이다(전부 불투명한 RGBA PNG 전부). */
export function judgeAlphaMeasurement(m: AlphaMeasurement): PrecutAssessment {
  if (m.borderCleared < PRECUT_MIN_BORDER_CLEARED) return no("BORDER_NOT_CLEARED", m);
  if (m.transparent < PRECUT_MIN_TRANSPARENT) return no("TOO_LITTLE_TRANSPARENT", m);
  if (m.subject < PRECUT_MIN_SUBJECT) return no("NOTHING_LEFT", m);
  return { verdict: "PRECUT", reason: "BACKGROUND_CLEARED", measured: m };
}

/** 알파 평면을 한 번 훑어 비율 셋을 센다.
 *
 * `alphaAt(x, y)` 로 받는 이유: **호출부마다 알파가 놓인 모양이 다르다.** PNG 는 이 파일이
 * 푼 행이고, 캔버스는 `RGBA` 가 4바이트 간격으로 늘어선 배열이다. 배열을 요구하면 한쪽이
 * 평면을 한 벌 더 만들어야 한다(12MP 면 12MB 다).
 *
 * ⛔ **여기서 판정하지 않는다.** 재는 것과 판정하는 것을 가른 이유는, **문턱을 시험이
 * 직접 단정할 수 있어야** 문턱을 바꾼 변이가 잡히기 때문이다. */
export function measureAlpha(
  width: number,
  height: number,
  alphaAt: (x: number, y: number) => number,
): AlphaMeasurement | null {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width < PRECUT_MIN_SIDE || height < PRECUT_MIN_SIDE) return null;

  const cx0 = Math.floor(width * CENTER_FROM);
  const cx1 = Math.floor(width * CENTER_TO);
  const cy0 = Math.floor(height * CENTER_FROM);
  const cy1 = Math.floor(height * CENTER_TO);

  let transparent = 0;
  let borderCount = 0;
  let borderClear = 0;
  let centerCount = 0;
  let centerOpaque = 0;

  for (let y = 0; y < height; y += 1) {
    const edgeRow = y === 0 || y === height - 1;
    const inCenterRow = y >= cy0 && y < cy1;
    for (let x = 0; x < width; x += 1) {
      const a = alphaAt(x, y);
      const clear = a < ALPHA_TRANSPARENT_BELOW;
      if (clear) transparent += 1;
      if (edgeRow || x === 0 || x === width - 1) {
        borderCount += 1;
        if (clear) borderClear += 1;
      }
      if (inCenterRow && x >= cx0 && x < cx1) {
        centerCount += 1;
        if (a >= ALPHA_OPAQUE_AT_LEAST) centerOpaque += 1;
      }
    }
  }

  if (borderCount === 0 || centerCount === 0) return null;
  return {
    width,
    height,
    transparent: transparent / (width * height),
    borderCleared: borderClear / borderCount,
    subject: centerOpaque / centerCount,
  };
}

// ── PNG 를 실제로 푼다 ──────────────────────────────────────────────────────

/** 그 색 종류가 픽셀당 쓰는 표본 수. */
const CHANNELS: Record<number, number> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

interface PngHead {
  width: number;
  height: number;
  bitDepth: number;
  colorType: number;
  interlace: number;
  idat: Uint8Array;
  trns: Uint8Array | null;
}

function parsePng(b: Uint8Array): PngHead | null {
  if (!isPng(b)) return null;
  const width = readU32(b, 16);
  const height = readU32(b, 20);
  const bitDepth = b[24]!;
  const colorType = b[25]!;
  if (b[26] !== 0 || b[27] !== 0) return null; // 압축·필터 방식은 규격상 0 뿐이다
  const interlace = b[28]!;

  let trns: Uint8Array | null = null;
  const idatParts: Uint8Array[] = [];
  let idatLen = 0;
  let at = 8;
  while (at + 8 <= b.length) {
    const len = readU32(b, at);
    const type = fourcc(b, at + 4);
    const from = at + 8;
    if (from + len > b.length) return null;
    if (type === "tRNS") trns = b.subarray(from, from + len);
    else if (type === "IDAT") {
      idatParts.push(b.subarray(from, from + len));
      idatLen += len;
    } else if (type === "IEND") break;
    at = from + len + 4;
  }
  if (idatLen === 0) return null;

  let idat: Uint8Array;
  if (idatParts.length === 1) idat = idatParts[0]!;
  else {
    idat = new Uint8Array(idatLen);
    let off = 0;
    for (const part of idatParts) {
      idat.set(part, off);
      off += part.length;
    }
  }
  return { width, height, bitDepth, colorType, interlace, idat, trns };
}

/** 행 필터를 되돌린다. `cur` 는 필터 바이트를 뺀 그 행, `prev` 는 되돌려진 앞 행. */
function unfilter(type: number, cur: Uint8Array, prev: Uint8Array, bpp: number): boolean {
  const n = cur.length;
  switch (type) {
    case 0:
      return true;
    case 1:
      for (let i = bpp; i < n; i += 1) cur[i] = (cur[i]! + cur[i - bpp]!) & 0xff;
      return true;
    case 2:
      for (let i = 0; i < n; i += 1) cur[i] = (cur[i]! + prev[i]!) & 0xff;
      return true;
    case 3:
      for (let i = 0; i < n; i += 1) {
        const a = i >= bpp ? cur[i - bpp]! : 0;
        cur[i] = (cur[i]! + ((a + prev[i]!) >> 1)) & 0xff;
      }
      return true;
    case 4:
      for (let i = 0; i < n; i += 1) {
        const a = i >= bpp ? cur[i - bpp]! : 0;
        const b = prev[i]!;
        const c = i >= bpp ? prev[i - bpp]! : 0;
        const p = a + b - c;
        const pa = p > a ? p - a : a - p;
        const pb = p > b ? p - b : b - p;
        const pc = p > c ? p - c : c - p;
        const pred = pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
        cur[i] = (cur[i]! + pred) & 0xff;
      }
      return true;
    default:
      return false;
  }
}

/** PNG 에서 **알파만** 읽어 비율 셋을 센다. 못 읽으면 사유를 돌려준다.
 *
 * ⚠️ **CRC 를 검사하지 않는다.** 여기서 하는 일은 파일 검증이 아니라 알파 읽기이고,
 * 깨진 파일은 `inflate` 나 필터 종류에서 대부분 걸려 `PIXELS_UNREAD` 가 된다 —
 * **못 읽으면 시트가 안 뜨는 쪽**이라 안전한 방향으로 떨어진다. */
export function measurePngAlpha(
  bytes: Uint8Array,
  maxPixels: number = PRECUT_MAX_PIXELS,
): { ok: true; measured: AlphaMeasurement } | { ok: false; reason: PrecutReason } {
  const png = parsePng(bytes);
  if (!png) return { ok: false, reason: "UNREADABLE" };
  // 인터레이스(Adam7)는 행이 순서대로 오지 않는다. **억지로 읽으면 알파가 엉뚱해진다.**
  if (png.interlace !== 0) return { ok: false, reason: "PIXELS_UNREAD" };
  if (png.width < PRECUT_MIN_SIDE || png.height < PRECUT_MIN_SIDE) {
    return { ok: false, reason: "TOO_SMALL" };
  }
  if (png.width * png.height > maxPixels) return { ok: false, reason: "TOO_MANY_PIXELS" };

  const channels = CHANNELS[png.colorType];
  if (!channels) return { ok: false, reason: "UNREADABLE" };
  const depth = png.bitDepth;
  // 팔레트의 1·2·4비트는 읽지 않는다. 색이 열여섯 이하인 장은 사진이 아니고, 비트 풀기를
  // 더하는 값보다 **안 읽고 `UNKNOWN` 으로 두는 값이 싸다.**
  if (depth !== 8 && depth !== 16) return { ok: false, reason: "PIXELS_UNREAD" };
  if (png.colorType === 3 && depth !== 8) return { ok: false, reason: "PIXELS_UNREAD" };

  const sample = depth >> 3; // 표본 한 개의 바이트 수(1 또는 2)
  const bpp = channels * sample;
  const rowBytes = png.width * bpp;
  const raw = inflateZlib(png.idat, png.height * (rowBytes + 1));
  if (!raw) return { ok: false, reason: "PIXELS_UNREAD" };

  // 색 종류마다 알파가 놓인 자리가 다르다.
  //   6(RGBA)·4(GA) — 픽셀의 **마지막 표본**이 알파다.
  //   3(팔레트)     — 알파가 표(`tRNS`)에 있고 픽셀은 그 표의 번호다.
  //   0(회색)·2(RGB) — `tRNS` 가 **투명할 색 하나**를 지정한다(색 열쇠).
  const trns = png.trns;
  const hasChannelAlpha = png.colorType === 4 || png.colorType === 6;
  if (!hasChannelAlpha && !trns) return { ok: false, reason: "PIXELS_UNREAD" };

  let key0 = -1;
  let key1 = -1;
  let key2 = -1;
  if (!hasChannelAlpha && png.colorType !== 3 && trns) {
    // 색 열쇠는 언제나 16비트로 적힌다. 8비트 장이면 하위 바이트를 쓴다.
    const at = (i: number) => (depth === 16 ? trns[i * 2]! : trns[i * 2 + 1]!);
    if (png.colorType === 0) {
      if (trns.length < 2) return { ok: false, reason: "PIXELS_UNREAD" };
      key0 = at(0);
    } else {
      if (trns.length < 6) return { ok: false, reason: "PIXELS_UNREAD" };
      key0 = at(0);
      key1 = at(1);
      key2 = at(2);
    }
  }

  const cur = new Uint8Array(rowBytes);
  const prev = new Uint8Array(rowBytes);
  let rowAt = 0;
  let y = 0;

  // **행을 한 번씩만 지나간다.** `measureAlpha` 에 `alphaAt` 을 넘기되, 그 함수가 요구하는
  // 순서(행 왼쪽→오른쪽, 위→아래)가 여기 푸는 순서와 같아서 앞 행을 들고 있을 필요가 없다.
  let failed = false;
  const alphaAt = (x: number, wantY: number): number => {
    if (failed) return 0;
    while (y <= wantY) {
      const filter = raw[rowAt]!;
      cur.set(raw.subarray(rowAt + 1, rowAt + 1 + rowBytes));
      if (!unfilter(filter, cur, prev, bpp)) {
        failed = true;
        return 0;
      }
      prev.set(cur);
      rowAt += rowBytes + 1;
      y += 1;
    }
    // `prev` 가 방금 되돌린 행이다.
    const at = x * bpp;
    if (hasChannelAlpha) return prev[at + bpp - sample]!;
    if (png.colorType === 3) {
      const idx = prev[at]!;
      return idx < trns!.length ? trns![idx]! : 255;
    }
    if (png.colorType === 0) return prev[at]! === key0 ? 0 : 255;
    return prev[at]! === key0 && prev[at + sample]! === key1 && prev[at + 2 * sample]! === key2
      ? 0
      : 255;
  };

  const measured = measureAlpha(png.width, png.height, alphaAt);
  if (failed || !measured) return { ok: false, reason: "PIXELS_UNREAD" };
  return { ok: true, measured };
}

// ── 들어오는 문 ────────────────────────────────────────────────────────────

/** **파일 바이트 하나를 보고 판정한다.** 클라이언트가 담기 전에 부르는 자리가 여기다.
 *
 * ⚠️ iOS 는 `asset.uri` 의 **파일**에서 읽어야 한다. `asset.base64` 는 언제나 JPEG 라
 * 이 함수가 늘 `NO_ALPHA_CHANNEL` 을 낸다(파일 머리말). */
export function judgePrecut(bytes: Uint8Array, maxPixels: number = PRECUT_MAX_PIXELS): PrecutAssessment {
  const capability = readAlphaCapability(bytes);
  if (capability === "NONE") return no("NO_ALPHA_CHANNEL");
  if (capability === "UNREADABLE") return unknown("UNREADABLE");
  // 알파를 담을 수 있다는 것까지다. **여기서 끝내면 그것이 ⓐ 이고, 거짓 양성이 쏟아진다.**
  if (!isPng(bytes)) return unknown("PIXELS_UNREAD"); // WebP 본문은 이 파일이 못 푼다
  const read = measurePngAlpha(bytes, maxPixels);
  if (!read.ok) return unknown(read.reason);
  return judgeAlphaMeasurement(read.measured);
}

/** **이미 푼 화소로 판정한다.** 웹이 부르는 자리다 — 캔버스가 어떤 형식이든 `RGBA` 로 주므로
 *  PNG 를 풀 필요가 없고 **형식 제한도 없다**(WebP 도 여기서는 판정된다).
 *
 * `rgba` 는 `ImageData.data` 그대로다(픽셀당 4바이트, 알파가 네 번째). */
export function judgePrecutFromRgba(
  width: number,
  height: number,
  rgba: Uint8Array | Uint8ClampedArray,
): PrecutAssessment {
  if (rgba.length < width * height * 4) return unknown("PIXELS_UNREAD");
  const measured = measureAlpha(width, height, (x, y) => rgba[(y * width + x) * 4 + 3]!);
  if (!measured) return unknown("TOO_SMALL");
  return judgeAlphaMeasurement(measured);
}

// ── inflate ────────────────────────────────────────────────────────────────
//
// ⛔ **왜 직접 들고 있나.** `DecompressionStream` 이 Workers 에도 브라우저에도 있지만
// **Hermes(React Native)에는 없다.** 판정이 앱·웹에서 같은 답을 내야 하는데(이 패키지의
// 존재 이유다) 한쪽에만 있는 API 에 기대면 앱에서만 `UNKNOWN` 이 난다.
//
// ⚠️ **시험이 `node:zlib` 을 오라클로 쓴다.** 고정물을 만드는 쪽은 `node:zlib`, 읽는 쪽은
// 이 코드다 — 두 구현이 독립이라야 같은 버그가 서로를 통과시키지 않는다.

class BitReader {
  private at = 0;
  private bit = 0;
  private readonly b: Uint8Array;
  // ⛔ 생성자 인자 속성(`constructor(private b: ...)`)을 쓰지 않는다. Node 의 타입 스트리핑이
  //    그 문법을 거절해서 **`packages/shared/test` 가 이 파일을 아예 불러오지 못한다**
  //    (`ERR_UNSUPPORTED_TYPESCRIPT_SYNTAX`). 시험이 못 도는 자리가 생기면 이 파일은
  //    타입체크만 지나는 코드가 된다.
  constructor(b: Uint8Array) {
    this.b = b;
  }
  /** 비트가 모자라면 `-1`. */
  read(n: number): number {
    let out = 0;
    for (let i = 0; i < n; i += 1) {
      if (this.at >= this.b.length) return -1;
      out |= ((this.b[this.at]! >> this.bit) & 1) << i;
      this.bit += 1;
      if (this.bit === 8) {
        this.bit = 0;
        this.at += 1;
      }
    }
    return out;
  }
  align(): void {
    if (this.bit !== 0) {
      this.bit = 0;
      this.at += 1;
    }
  }
  bytes(n: number): Uint8Array | null {
    if (this.at + n > this.b.length) return null;
    const out = this.b.subarray(this.at, this.at + n);
    this.at += n;
    return out;
  }
}

interface Huffman {
  counts: Int32Array;
  symbols: Int32Array;
}

/** 표준 허프만 표. 길이별 개수와 그 순서의 기호만 들고 있으면 `decodeSymbol` 이 푼다. */
function buildHuffman(lengths: Uint8Array): Huffman {
  const counts = new Int32Array(16);
  for (const len of lengths) counts[len] = counts[len]! + 1;
  counts[0] = 0;
  const offsets = new Int32Array(16);
  for (let len = 1; len < 16; len += 1) offsets[len] = offsets[len - 1]! + counts[len - 1]!;
  const symbols = new Int32Array(lengths.length);
  for (let sym = 0; sym < lengths.length; sym += 1) {
    const len = lengths[sym]!;
    if (len > 0) {
      symbols[offsets[len]!] = sym;
      offsets[len] = offsets[len]! + 1;
    }
  }
  return { counts, symbols };
}

function decodeSymbol(br: BitReader, h: Huffman): number {
  let code = 0;
  let first = 0;
  let index = 0;
  for (let len = 1; len < 16; len += 1) {
    const bit = br.read(1);
    if (bit < 0) return -1;
    code |= bit;
    const count = h.counts[len]!;
    if (code - first < count) return h.symbols[index + (code - first)]!;
    index += count;
    first = (first + count) << 1;
    code <<= 1;
  }
  return -1;
}

const LENGTH_BASE = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131,
  163, 195, 227, 258,
];
const LENGTH_EXTRA = [
  0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0,
];
const DIST_BASE = [
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049,
  3073, 4097, 6145, 8193, 12289, 16385, 24577,
];
const DIST_EXTRA = [
  0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13,
];
const CODE_LENGTH_ORDER = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];

let fixedLit: Huffman | null = null;
let fixedDist: Huffman | null = null;

function fixedTables(): { lit: Huffman; dist: Huffman } {
  if (!fixedLit || !fixedDist) {
    const lit = new Uint8Array(288);
    for (let i = 0; i < 144; i += 1) lit[i] = 8;
    for (let i = 144; i < 256; i += 1) lit[i] = 9;
    for (let i = 256; i < 280; i += 1) lit[i] = 7;
    for (let i = 280; i < 288; i += 1) lit[i] = 8;
    fixedLit = buildHuffman(lit);
    fixedDist = buildHuffman(new Uint8Array(30).fill(5));
  }
  return { lit: fixedLit, dist: fixedDist };
}

/** zlib 껍데기(2바이트 머리 + 끝의 adler32)를 벗기고 푼다. `expected` 는 **IHDR 에서 계산한
 *  정확한 바이트 수**다 — 미리 알기 때문에 버퍼를 늘려 가며 복사하지 않는다.
 *
 * ⛔ **`expected` 보다 많이 나오면 버린다.** 길이를 안 맞추면 깨진 스트림이 메모리를 먹는다. */
export function inflateZlib(input: Uint8Array, expected: number): Uint8Array | null {
  if (input.length < 2) return null;
  const cmf = input[0]!;
  const flg = input[1]!;
  if ((cmf & 0x0f) !== 8) return null; // deflate 가 아니다
  if (((cmf << 8) | flg) % 31 !== 0) return null;
  if ((flg & 0x20) !== 0) return null; // 사전 지정 — PNG 에는 없다
  return inflateRaw(input.subarray(2), expected);
}

export function inflateRaw(input: Uint8Array, expected: number): Uint8Array | null {
  const out = new Uint8Array(expected);
  const br = new BitReader(input);
  let at = 0;

  for (;;) {
    const last = br.read(1);
    if (last < 0) return null;
    const type = br.read(2);
    if (type < 0) return null;

    if (type === 0) {
      br.align();
      const header = br.bytes(4);
      if (!header) return null;
      const len = header[0]! | (header[1]! << 8);
      const nlen = header[2]! | (header[3]! << 8);
      if ((len ^ 0xffff) !== nlen) return null;
      const block = br.bytes(len);
      if (!block || at + len > expected) return null;
      out.set(block, at);
      at += len;
    } else if (type === 1 || type === 2) {
      let lit: Huffman;
      let dist: Huffman;
      if (type === 1) {
        const fixed = fixedTables();
        lit = fixed.lit;
        dist = fixed.dist;
      } else {
        const hlit = br.read(5);
        const hdist = br.read(5);
        const hclen = br.read(4);
        if (hlit < 0 || hdist < 0 || hclen < 0) return null;
        const nLit = hlit + 257;
        const nDist = hdist + 1;
        const clLengths = new Uint8Array(19);
        for (let i = 0; i < hclen + 4; i += 1) {
          const v = br.read(3);
          if (v < 0) return null;
          clLengths[CODE_LENGTH_ORDER[i]!] = v;
        }
        const clTable = buildHuffman(clLengths);
        const lengths = new Uint8Array(nLit + nDist);
        let i = 0;
        while (i < lengths.length) {
          const sym = decodeSymbol(br, clTable);
          if (sym < 0) return null;
          if (sym < 16) {
            lengths[i++] = sym;
          } else if (sym === 16) {
            if (i === 0) return null;
            const n = br.read(2);
            if (n < 0) return null;
            const prev = lengths[i - 1]!;
            for (let k = 0; k < n + 3; k += 1) {
              if (i >= lengths.length) return null;
              lengths[i++] = prev;
            }
          } else {
            const n = sym === 17 ? br.read(3) : br.read(7);
            if (n < 0) return null;
            const run = sym === 17 ? n + 3 : n + 11;
            for (let k = 0; k < run; k += 1) {
              if (i >= lengths.length) return null;
              lengths[i++] = 0;
            }
          }
        }
        lit = buildHuffman(lengths.subarray(0, nLit));
        dist = buildHuffman(lengths.subarray(nLit));
      }

      for (;;) {
        const sym = decodeSymbol(br, lit);
        if (sym < 0) return null;
        if (sym < 256) {
          if (at >= expected) return null;
          out[at++] = sym;
        } else if (sym === 256) {
          break;
        } else {
          const li = sym - 257;
          if (li >= LENGTH_BASE.length) return null;
          const extraLen = br.read(LENGTH_EXTRA[li]!);
          if (extraLen < 0) return null;
          const length = LENGTH_BASE[li]! + extraLen;
          const dsym = decodeSymbol(br, dist);
          if (dsym < 0 || dsym >= DIST_BASE.length) return null;
          const extraDist = br.read(DIST_EXTRA[dsym]!);
          if (extraDist < 0) return null;
          const distance = DIST_BASE[dsym]! + extraDist;
          if (distance > at || at + length > expected) return null;
          let from = at - distance;
          for (let k = 0; k < length; k += 1) out[at++] = out[from++]!;
        }
      }
    } else {
      return null;
    }

    if (last === 1) break;
  }

  // **다 채우지 못했으면 실패다.** 덜 푼 버퍼는 뒤쪽이 0 이라 **통째로 투명한 장**처럼
  // 보인다 — 그것이 바로 잘못 띄우는 경로다.
  return at === expected ? out : null;
}
